import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';

const useStyles = makeStyles((theme) => ({
  root: (props) => {
    const {
      direction,
      justify,
      align,
      gap,
      wrap,
    } = props;

    return {
      display: 'flex',
      flexDirection: direction,
      justifyContent: justify,
      alignItems: align,
      flexWrap: wrap,
      gap: typeof gap === 'number' ? theme.spacing(gap) : gap || 0,
    };
  },
}));

/**
 * A base Flex component that serves as a flexible wrapper for common
 * flex-related styles (direction, justification, alignment, wrapping, gap, etc.).
 *
 * @param {object} props - Component props
 * @param {'row' | 'row-reverse' | 'column' | 'column-reverse'} [props.direction='row'] - Controls flex-direction.
 * @param {'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'} [props.justify='flex-start'] - Controls justify-content.
 * @param {'stretch' | 'flex-start' | 'flex-end' | 'center' | 'baseline'} [props.align='center'] - Controls align-items.
 * @param {number | string} [props.gap=0] - Gap between flex items. If it's a number, it uses theme.spacing.
 * @param {'nowrap' | 'wrap' | 'wrap-reverse'} [props.wrap='nowrap'] - Controls flex-wrap.
 * @param {React.ElementType} [props.component=Box] - The component used for the root node.
 * @param {React.ReactNode} props.children - Child elements to be rendered inside the Flex container.
 * @param {object} [props.style] - Optional inline styles applied to the container.
 * @param {string} [props.className] - Optional custom className for the container.
 */
export const Flex = ({
  direction = 'row',
  justify = 'flex-start',
  align = 'center',
  gap = 0,
  wrap = 'nowrap',
  style,
  className,
  component,
  children,
  ...rest
}) => {
  const classes = useStyles({ direction, justify, align, gap, wrap });

  const Component = component || Box;

  return (
    <Component
      className={`${classes.root} ${className || ''}`}
      style={style}
      {...rest}
    >
      {children}
    </Component>
  );
};

/**
 * A Group component that arranges items horizontally by default
 *
 * @param {object} props - Component props
 * @param {'row' | 'row-reverse' | 'column' | 'column-reverse'} [props.direction='row'] - Controls flex-direction.
 * @param {'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'} [props.justify='flex-start'] - Controls justify-content.
 * @param {'stretch' | 'flex-start' | 'flex-end' | 'center' | 'baseline'} [props.align='center'] - Controls align-items.
 * @param {number | string} [props.gap=2] - Gap between items. If it's a number, it uses theme.spacing.
 * @param {'nowrap' | 'wrap' | 'wrap-reverse'} [props.wrap='nowrap'] - Controls flex-wrap.
 * @param {React.ElementType} [props.component=Box] - The component used for the root node.
 * @param {React.ReactNode} props.children - Child elements to be rendered inside the container.
 * @param {object} [props.style] - Optional inline styles applied to the container.
 * @param {string} [props.className] - Optional custom className for the container.
 */
export const Group = ({
  direction = 'row',
  align = 'center',
  gap = 1,
  wrap = 'nowrap',
  ...props
}) => {
  return (
    <Flex
      direction={direction}
      align={align}
      gap={gap}
      wrap={wrap}
      {...props}
    />
  );
};

/**
 * A Stack component that arranges items vertically by default,
 *
 * @param {object} props - Component props
 * @param {'row' | 'row-reverse' | 'column' | 'column-reverse'} [props.direction='column'] - Controls flex-direction.
 * @param {'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'} [props.justify='flex-start'] - Controls justify-content.
 * @param {'stretch' | 'flex-start' | 'flex-end' | 'center' | 'baseline'} [props.align='stretch'] - Controls align-items.
 * @param {number | string} [props.gap=2] - Gap between items. If it's a number, it uses theme.spacing.
 * @param {'nowrap' | 'wrap' | 'wrap-reverse'} [props.wrap='nowrap'] - Controls flex-wrap.
 * @param {React.ElementType} [props.component=Box] - The component used for the root node.
 * @param {React.ReactNode} props.children - Child elements to be rendered inside the container.
 * @param {object} [props.style] - Optional inline styles applied to the container.
 * @param {string} [props.className] - Optional custom className for the container.
 */
export const Stack = ({
  direction = 'column',
  align = 'stretch',
  gap = 1,
  wrap = 'nowrap',
  ...props
}) => {
  return (
    <Flex
      direction={direction}
      align={align}
      gap={gap}
      wrap={wrap}
      {...props}
    />
  );
};
